/**
 * SettingsPage — API Key Management
 * 
 * Allows users to configure their own AI provider API keys.
 * Keys are encrypted server-side and scoped per-user.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { AppLayout } from '../../components/layout';
import styles from './SettingsPage.module.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';

interface ProviderConfig {
  id: string;
  name: string;
  description: string;
  models: string[];
  placeholder: string;
  iconClass: string;
  iconLabel: string;
}

const PROVIDERS: ProviderConfig[] = [
  {
    id: 'github',
    name: 'GitHub Models',
    description: 'Access GPT-4o, Phi, Llama and more via GitHub Models marketplace',
    models: ['gpt-4o', 'gpt-4o-mini', 'o1-preview', 'o1-mini', 'Phi-3.5-MoE-instruct'],
    placeholder: 'ghp_xxxxxxxxxxxxxxxxxxxx',
    iconClass: 'providerIconGithub',
    iconLabel: 'GH',
  },
  {
    id: 'openai',
    name: 'OpenAI',
    description: 'Direct access to GPT-4.1, GPT-4o, o1, o3 and other OpenAI models',
    models: ['gpt-4.1', 'gpt-4.1-mini', 'gpt-4.1-nano', 'gpt-4o', 'gpt-4o-mini', 'o1', 'o1-mini', 'o3-mini'],
    placeholder: 'sk-xxxxxxxxxxxxxxxxxxxx',
    iconClass: 'providerIconOpenai',
    iconLabel: 'AI',
  },
  {
    id: 'anthropic',
    name: 'Anthropic (Claude)',
    description: 'Access Claude Opus 4, Claude Sonnet 4, Claude 3.5 and other Anthropic models',
    models: ['claude-opus-4-20250514', 'claude-sonnet-4-20250514', 'claude-sonnet-4-5-20250929', 'claude-3-5-sonnet-20241022', 'claude-3-haiku-20240307'],
    placeholder: 'sk-ant-xxxxxxxxxxxxxxxxxxxx',
    iconClass: 'providerIconAnthropic',
    iconLabel: 'CL',
  },
  {
    id: 'grok',
    name: 'xAI (Grok)',
    description: 'Access Grok-2, Grok-2 Vision and other xAI models',
    models: ['grok-2', 'grok-2-vision', 'grok-beta'],
    placeholder: 'xai-xxxxxxxxxxxxxxxxxxxx',
    iconClass: 'providerIconGrok',
    iconLabel: 'XA',
  },
];

interface SavedKey {
  provider: string;
  isConfigured: boolean;
  maskedKey: string;
}

interface ServerProviderStatus {
  configured: boolean;
  model: string;
}

interface ServerStatus {
  ready: boolean;
  defaultProvider: string | null;
  providers: Record<string, ServerProviderStatus>;
}

// Model pricing data (matches backend LLM_PRICING)
const MODEL_PRICING: Record<string, { input: number; output: number }> = {
  'gpt-4.1': { input: 0.002, output: 0.008 },
  'gpt-4.1-mini': { input: 0.0004, output: 0.0016 },
  'gpt-4.1-nano': { input: 0.0001, output: 0.0004 },
  'gpt-4o': { input: 0.0025, output: 0.01 },
  'gpt-4o-mini': { input: 0.00015, output: 0.0006 },
  'o1': { input: 0.015, output: 0.06 },
  'o1-mini': { input: 0.003, output: 0.012 },
  'o1-preview': { input: 0.015, output: 0.06 },
  'o3-mini': { input: 0.0011, output: 0.0044 },
  'claude-opus-4-20250514': { input: 0.015, output: 0.075 },
  'claude-sonnet-4-20250514': { input: 0.003, output: 0.015 },
  'claude-sonnet-4-5-20250929': { input: 0.003, output: 0.015 },
  'claude-3-5-sonnet-20241022': { input: 0.003, output: 0.015 },
  'claude-3-haiku-20240307': { input: 0.0008, output: 0.004 },
  'grok-2': { input: 0.002, output: 0.01 },
  'grok-2-vision': { input: 0.002, output: 0.01 },
  'grok-beta': { input: 0.005, output: 0.015 },
  'Phi-3.5-MoE-instruct': { input: 0.0004, output: 0.0004 },
};

export const SettingsPage: React.FC = () => {
  const [savedKeys, setSavedKeys] = useState<SavedKey[]>([]);
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [serverStatus, setServerStatus] = useState<ServerStatus | null>(null);
  const [defaultModel, setDefaultModel] = useState<string>('');
  const [settingDefault, setSettingDefault] = useState<string | null>(null);

  const getToken = () => localStorage.getItem('bringiq_token') || '';

  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // Load existing key config, server provider status, and preferences
  useEffect(() => {
    const loadKeys = async () => {
      try {
        const res = await fetch(`${API_BASE}/user-settings/api-keys`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        const data = await res.json();
        if (data.success) {
          setSavedKeys(data.data.providers || []);
        }
      } catch {
        // Keys not loaded — user hasn't saved any yet
      }
    };

    const loadServerStatus = async () => {
      try {
        const res = await fetch(`${API_BASE}/models/status`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        const data = await res.json();
        if (data.success) {
          setServerStatus(data.data);
        }
      } catch {
        // Server status not available
      }
    };

    const loadPreferences = async () => {
      try {
        const res = await fetch(`${API_BASE}/user-settings/preferences`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        const data = await res.json();
        if (data.success && data.data.preferences?.defaultModel) {
          setDefaultModel(data.data.preferences.defaultModel as string);
        }
      } catch {
        // Preferences not loaded
      }
    };

    loadKeys();
    loadServerStatus();
    loadPreferences();
  }, []);

  const handleSetDefaultModel = async (model: string) => {
    setSettingDefault(model);
    try {
      const res = await fetch(`${API_BASE}/user-settings/preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ defaultModel: model }),
      });
      const data = await res.json();
      if (data.success) {
        setDefaultModel(model);
        showToast(`Default model set to ${model}`, 'success');
      } else {
        showToast('Failed to set default model', 'error');
      }
    } catch {
      showToast('Failed to set default model', 'error');
    } finally {
      setSettingDefault(null);
    }
  };

  const isConfigured = (providerId: string): boolean => {
    return savedKeys.some(k => k.provider === providerId && k.isConfigured);
  };

  const isServerConfigured = (providerId: string): boolean => {
    return serverStatus?.providers?.[providerId]?.configured || false;
  };

  const isAvailable = (providerId: string): boolean => {
    return isConfigured(providerId) || isServerConfigured(providerId);
  };

  const getMaskedKey = (providerId: string): string => {
    const key = savedKeys.find(k => k.provider === providerId);
    return key?.maskedKey || '';
  };

  const handleSave = async (providerId: string) => {
    const apiKey = inputs[providerId];
    if (!apiKey?.trim()) return;

    setSaving(providerId);
    try {
      const res = await fetch(`${API_BASE}/user-settings/api-keys`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          keys: [{ provider: providerId, apiKey: apiKey.trim(), isConfigured: true }],
        }),
      });

      const data = await res.json();
      if (data.success) {
        setSavedKeys(data.data.providers || []);
        setInputs(prev => ({ ...prev, [providerId]: '' }));
        showToast(`${PROVIDERS.find(p => p.id === providerId)?.name} API key saved!`, 'success');
      } else {
        showToast(data.error?.message || 'Failed to save', 'error');
      }
    } catch {
      showToast('Failed to save API key', 'error');
    } finally {
      setSaving(null);
    }
  };

  const handleRemove = async (providerId: string) => {
    try {
      const res = await fetch(`${API_BASE}/user-settings/api-keys/${providerId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` },
      });

      if (res.ok) {
        setSavedKeys(prev => prev.filter(k => k.provider !== providerId));
        showToast(`${PROVIDERS.find(p => p.id === providerId)?.name} API key removed`, 'success');
      }
    } catch {
      showToast('Failed to remove API key', 'error');
    }
  };

  return (
    <AppLayout>
      <div className={styles.settingsPage}>
        <div className={styles.settingsContent}>
          <div className={styles.settingsHeader}>
            <h1>AI Provider Settings</h1>
            <p>Configure your API keys to enable AI models. Your keys are encrypted and stored securely.</p>
          </div>

          {PROVIDERS.map(provider => {
            const configured = isConfigured(provider.id);
            return (
              <div
                key={provider.id}
                className={`${styles.providerCard} ${configured ? styles.providerCardConfigured : ''}`}
              >
                <div className={styles.providerHeader}>
                  <div className={styles.providerInfo}>
                    <div className={`${styles.providerIcon} ${styles[provider.iconClass]}`}>
                      {provider.iconLabel}
                    </div>
                    <div>
                      <div className={styles.providerName}>{provider.name}</div>
                      <div className={styles.providerDescription}>{provider.description}</div>
                    </div>
                  </div>
                  <span className={`${styles.badge} ${configured ? styles.badgeConfigured : styles.badgeNotConfigured}`}>
                    {configured ? 'Configured' : 'Not configured'}
                  </span>
                </div>

                <div className={styles.inputGroup}>
                  <input
                    type="password"
                    className={styles.apiKeyInput}
                    placeholder={configured ? getMaskedKey(provider.id) : provider.placeholder}
                    value={inputs[provider.id] || ''}
                    onChange={e => setInputs(prev => ({ ...prev, [provider.id]: e.target.value }))}
                    onKeyDown={e => e.key === 'Enter' && handleSave(provider.id)}
                  />
                  <button
                    className={styles.saveBtn}
                    disabled={!inputs[provider.id]?.trim() || saving === provider.id}
                    onClick={() => handleSave(provider.id)}
                  >
                    {saving === provider.id ? 'Saving...' : configured ? 'Update' : 'Save'}
                  </button>
                  {configured && (
                    <button className={styles.removeBtn} onClick={() => handleRemove(provider.id)}>
                      Remove
                    </button>
                  )}
                </div>

                <div className={styles.modelList}>
                  {provider.models.map(model => (
                    <span key={model} className={styles.modelTag}>{model}</span>
                  ))}
                </div>
              </div>
            );
          })}

          <div className={styles.statusBar}>
            <h3>Provider Status</h3>
            <div className={styles.statusGrid}>
              {PROVIDERS.map(p => {
                const userKey = isConfigured(p.id);
                const serverKey = isServerConfigured(p.id);
                const available = userKey || serverKey;
                return (
                  <div key={p.id} className={styles.statusItem}>
                    <span className={`${styles.statusDot} ${available ? styles.statusDotActive : styles.statusDotInactive}`} />
                    <span className={styles.statusLabel}>{p.name}</span>
                    <span className={styles.statusSource}>
                      {userKey && serverKey ? 'Your Key + Server' : userKey ? 'Your Key' : serverKey ? 'BrainIQ Key' : 'Not available'}
                    </span>
                  </div>
                );
              })}
            </div>
            <p className={styles.statusNote}>
              Models with "BrainIQ Key" use the platform's API key — usage costs will appear on your Analytics dashboard.
              Provide your own key to use your own quota.
            </p>
          </div>

          <div className={styles.modelCatalog}>
            <h2>Available Models</h2>
            <p className={styles.catalogDescription}>
              All models you can use in chat. Green models are available. Click "Set Default" to make a model your default for all new chats.
              {defaultModel && (
                <span className={styles.currentDefault}> Current default: <strong>{defaultModel}</strong></span>
              )}
            </p>
            {PROVIDERS.map(provider => {
              const available = isAvailable(provider.id);
              const source = isConfigured(provider.id) ? 'your key' : isServerConfigured(provider.id) ? 'BrainIQ key' : null;
              return (
                <div key={provider.id} className={styles.catalogSection}>
                  <div className={styles.catalogSectionHeader}>
                    <div className={`${styles.providerIcon} ${styles[provider.iconClass]}`} style={{ width: 28, height: 28, fontSize: '0.8rem' }}>
                      {provider.iconLabel}
                    </div>
                    <span className={styles.catalogProviderName}>{provider.name}</span>
                    {source && <span className={styles.catalogSource}>via {source}</span>}
                  </div>
                  <div className={styles.catalogModels}>
                    {provider.models.map(model => {
                      const pricing = MODEL_PRICING[model];
                      const isDefault = defaultModel === model;
                      return (
                        <div
                          key={model}
                          className={`${styles.catalogModelCard} ${available ? styles.catalogModelAvailable : styles.catalogModelDisabled} ${isDefault ? styles.catalogModelDefault : ''}`}
                        >
                          <div className={styles.catalogModelName}>{model}</div>
                          {pricing && (
                            <div className={styles.catalogModelPricing}>
                              <span>${pricing.input}/1K in</span>
                              <span>${pricing.output}/1K out</span>
                            </div>
                          )}
                          <div className={styles.catalogModelFooter}>
                            <span className={`${styles.catalogModelStatus} ${available ? styles.catalogModelStatusActive : ''}`}>
                              {isDefault ? '★ Default' : available ? 'Available' : 'Key needed'}
                            </span>
                            {available && !isDefault && (
                              <button
                                className={styles.setDefaultBtn}
                                disabled={settingDefault === model}
                                onClick={() => handleSetDefaultModel(model)}
                              >
                                {settingDefault === model ? '...' : 'Set Default'}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {toast && (
        <div className={`${styles.toast} ${toast.type === 'success' ? styles.toastSuccess : styles.toastError}`}>
          {toast.message}
        </div>
      )}
    </AppLayout>
  );
};

export default SettingsPage;
